import { ThemeProvider, alpha, createTheme } from '@mui/material/styles';
import type { CSSProperties, ThemeOptions } from '@mui/material/styles';
import { colorSchemes, typography, shadows, shape, gray, brand, green, red } from './themePrimitives';
import { Fragment, ReactNode, useMemo } from 'react';
import { buttonBaseClasses, chipClasses, ChipProps, iconButtonClasses, IconProps, LinkProps, svgIconClasses, tabClasses, toggleButtonClasses, toggleButtonGroupClasses, typographyClasses } from '@mui/material';
import CheckBoxOutlineBlankRoundedIcon from '@mui/icons-material/CheckBoxOutlineBlankRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';

interface AppThemeProps {
  children: ReactNode;
  /**
   * This is for the docs site. You can ignore it or remove it.
   */
  disableCustomTheme?: boolean;
  themeComponents?: ThemeOptions['components'];
}

export default function AppTheme(props: AppThemeProps) {
  const { children, disableCustomTheme } = props;
  const theme = useMemo(() => {
    const components = {
      MuiButtonBase: {
        defaultProps: {
          disableTouchRipple: true,
          disableRipple: true,
        },
        styleOverrides: {
          root: (props: any) => ({
            boxSizing: 'border-box',
            transition: 'all 100ms ease-in',
            '&:focus-visible': {
              outline: `3px solid ${alpha(props.theme.palette.primary.main, 0.5)}`,
              outlineOffset: '2px',
            },
          } as CSSProperties),
        },
      },
      MuiButton: {
        styleOverrides: {
          root: (props: any) => ({
            boxShadow: 'none',
            borderRadius: (props.theme.vars || props.theme).shape.borderRadius,
            textTransform: 'none',
            variants: [
              {
                props: {
                  size: 'small',
                },
                style: {
                  height: '2.25rem',
                  padding: '8px 12px',
                },
              },
              {
                props: {
                  size: 'medium',
                },
                style: {
                  height: '2.5rem',
                },
              },
              {
                props: {
                  color: 'primary',
                  variant: 'contained',
                },
                style: {
                  color: 'white',
                  backgroundColor: gray[900],
                  backgroundImage: `linear-gradient(to bottom, ${gray[700]}, ${gray[800]})`,
                  boxShadow: `inset 0 1px 0 ${gray[600]}, inset 0 -1px 0 1px hsl(220, 0%, 0%)`,
                  border: `1px solid ${gray[700]}`,
                  '&:hover': {
                    backgroundImage: 'none',
                    backgroundColor: gray[700],
                    boxShadow: 'none',
                  },
                  '&:active': {
                    backgroundColor: gray[800],
                  },
                  '&:disabled': {
                    backgroundColor: alpha('rgb(0, 0, 0)', 0.12),
                    backgroundImage: 'none',
                    borderStyle: 'none',
                    color: alpha('rgb(0, 0, 0)', 0.26)
                  },
                  ...props.theme.applyStyles('dark', {
                    color: 'black',
                    backgroundColor: gray[50],
                    backgroundImage: `linear-gradient(to bottom, ${gray[100]}, ${gray[50]})`,
                    boxShadow: 'inset 0 -1px 0  hsl(220, 30%, 80%)',
                    border: `1px solid ${gray[50]}`,
                    '&:hover': {
                      backgroundImage: 'none',
                      backgroundColor: gray[300],
                      boxShadow: 'none',
                    },
                    '&:active': {
                      backgroundColor: gray[400],
                    },
                    '&:disabled': {
                      backgroundColor: alpha('rgb(255, 255, 255)', 0.12),
                      backgroundImage: 'none',
                      borderStyle: 'none',
                      color: alpha('rgb(255, 255, 255)', 0.3)
                    }
                  }),
                },
              },
              {
                props: {
                  color: 'secondary',
                  variant: 'contained',
                },
                style: {
                  color: 'white',
                  backgroundColor: brand[300],
                  backgroundImage: `linear-gradient(to bottom, ${alpha(brand[400], 0.8)}, ${brand[500]})`,
                  boxShadow: `inset 0 2px 0 ${alpha(brand[200], 0.2)}, inset 0 -2px 0 ${alpha(brand[700], 0.4)}`,
                  border: `1px solid ${brand[500]}`,
                  '&:hover': {
                    backgroundColor: brand[700],
                    boxShadow: 'none',
                  },
                  '&:active': {
                    backgroundColor: brand[700],
                    backgroundImage: 'none',
                  },
                  '&:disabled': {
                    backgroundColor: alpha('rgb(0, 0, 0)', 0.12),
                    backgroundImage: 'none',
                    borderStyle: 'none',
                    color: alpha('rgb(0, 0, 0)', 0.26)
                  },
                  ...props.theme.applyStyles('dark', {
                    color: brand[50],
                    border: '1px solid',
                    borderColor: brand[900],
                    backgroundColor: alpha(brand[900], 0.3),
                    '&:hover': {
                      borderColor: brand[700],
                      backgroundColor: alpha(brand[900], 0.6),
                    },
                    '&:active': {
                      backgroundColor: alpha(brand[900], 0.5),
                    },
                    '&:disabled': {
                      backgroundColor: alpha('rgb(255, 255, 255)', 0.12),
                      backgroundImage: 'none',
                      borderStyle: 'none',
                      color: alpha('rgb(255, 255, 255)', 0.3)
                    }
                  }),
                },
              },
              {
                props: {
                  variant: 'outlined',
                },
                style: {
                  color: (props.theme.vars || props.theme).palette.text.primary,
                  border: '1px solid',
                  borderColor: gray[200],
                  backgroundColor: alpha(gray[50], 0.3),
                  '&:hover': {
                    backgroundColor: gray[100],
                    borderColor: gray[300],
                  },
                  '&:active': {
                    backgroundColor: gray[200],
                  },
                  ...props.theme.applyStyles('dark', {
                    backgroundColor: gray[800],
                    borderColor: gray[700],

                    '&:hover': {
                      backgroundColor: gray[900],
                      borderColor: gray[600],
                    },
                    '&:active': {
                      backgroundColor: gray[900],
                    },
                  }),
                },
              },
              {
                props: {
                  color: 'secondary',
                  variant: 'outlined',
                },
                style: {
                  color: brand[700],
                  border: '1px solid',
                  borderColor: brand[200],
                  backgroundColor: brand[50],
                  '&:hover': {
                    backgroundColor: brand[100],
                    borderColor: brand[400],
                  },
                  '&:active': {
                    backgroundColor: alpha(brand[200], 0.7),
                  },
                  ...props.theme.applyStyles('dark', {
                    color: brand[50],
                    border: '1px solid',
                    borderColor: brand[900],
                    backgroundColor: alpha(brand[900], 0.3),
                    '&:hover': {
                      borderColor: brand[700],
                      backgroundColor: alpha(brand[900], 0.6),
                    },
                    '&:active': {
                      backgroundColor: alpha(brand[900], 0.5),
                    },
                    '&:disabled': {
                      backgroundColor: alpha('rgb(255, 255, 255)', 0.12),
                      backgroundImage: 'none',
                      borderStyle: 'none',
                      color: alpha('rgb(255, 255, 255)', 0.3)
                    }
                  }),
                },
              },
              {
                props: {
                  variant: 'text',
                },
                style: {
                  color: gray[600],
                  '&:hover': {
                    backgroundColor: gray[100],
                  },
                  '&:active': {
                    backgroundColor: gray[200],
                  },
                  ...props.theme.applyStyles('dark', {
                    color: gray[50],
                    '&:hover': {
                      backgroundColor: gray[700],
                    },
                    '&:active': {
                      backgroundColor: alpha(gray[700], 0.7),
                    },
                  }),
                },
              },
              {
                props: {
                  color: 'secondary',
                  variant: 'text',
                },
                style: {
                  color: brand[700],
                  '&:hover': {
                    backgroundColor: alpha(brand[100], 0.5),
                  },
                  '&:active': {
                    backgroundColor: alpha(brand[200], 0.7),
                  },
                  ...props.theme.applyStyles('dark', {
                    color: brand[100],
                    '&:hover': {
                      backgroundColor: alpha(brand[900], 0.5),
                    },
                    '&:active': {
                      backgroundColor: alpha(brand[900], 0.3),
                    },
                  }),
                },
              },
            ],
          }),
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: (props: any) => ({
            boxShadow: 'none',
            borderRadius: (props.theme.vars || props.theme).shape.borderRadius,
            textTransform: 'none',
            fontWeight: props.theme.typography.fontWeightMedium,
            letterSpacing: 0,
            color: (props.theme.vars || props.theme).palette.text.primary,
            border: '1px solid ',
            borderColor: gray[200],
            backgroundColor: alpha(gray[50], 0.3),
            '&:hover': {
              backgroundColor: gray[100],
              borderColor: gray[300],
            },
            '&:active': {
              backgroundColor: gray[200],
            },
            ...props.theme.applyStyles('dark', {
              backgroundColor: gray[800],
              borderColor: gray[700],
              '&:hover': {
                backgroundColor: gray[900],
                borderColor: gray[600],
              },
              '&:active': {
                backgroundColor: gray[900],
              },
            }),
          }),
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: (props: any) => ({
            borderRadius: '10px',
            boxShadow: `0 4px 16px ${alpha(gray[400], 0.2)}`,
            [`& .${toggleButtonGroupClasses.selected}`]: {
              color: brand[500],
            },
            ...props.theme.applyStyles('dark', {
              [`& .${toggleButtonGroupClasses.selected}`]: {
                color: '#fff',
              },
              boxShadow: `0 4px 16px ${alpha(brand[700], 0.5)}`,
            }),
          }),
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: (props: any) => ({
            padding: '12px 16px',
            textTransform: 'none',
            borderRadius: '10px',
            fontWeight: 500,
            ...props.theme.applyStyles('dark', {
              color: gray[400],
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.5)',
              [`&.${toggleButtonClasses.selected}`]: {
                color: brand[300],
              },
            }),
          }),
        },
      },
      MuiCheckbox: {
        defaultProps: {
          disableRipple: true,
          icon: (
            <CheckBoxOutlineBlankRoundedIcon sx={{ color: 'hsla(210, 0%, 0%, 0.0)' }} />
          ),
          checkedIcon: <CheckRoundedIcon sx={{ height: 14, width: 14 }} />,
          indeterminateIcon: <RemoveRoundedIcon sx={{ height: 14, width: 14 }} />,
        },
        styleOverrides: {
          root: (props: any) => ({
            margin: 10,
            height: 16,
            width: 16,
            borderRadius: 5,
            border: '1px solid ',
            borderColor: alpha(gray[300], 0.8),
            boxShadow: '0 0 0 1.5px hsla(210, 0%, 0%, 0.04) inset',
            backgroundColor: alpha(gray[100], 0.4),
            transition: 'border-color, background-color, 120ms ease-in',
            '&:hover': {
              borderColor: brand[300],
            },
            '&.Mui-focusVisible': {
              outline: `3px solid ${alpha(brand[500], 0.5)}`,
              outlineOffset: '2px',
              borderColor: brand[400],
            },
            '&.Mui-checked': {
              color: 'white',
              backgroundColor: brand[500],
              borderColor: brand[500],
              boxShadow: `none`,
              '&:hover': {
                backgroundColor: brand[600],
              },
            },
            ...props.theme.applyStyles('dark', {
              borderColor: alpha(gray[700], 0.8),
              boxShadow: '0 0 0 1.5px hsl(210, 0%, 0%) inset',
              backgroundColor: alpha(gray[900], 0.8),
              '&:hover': {
                borderColor: brand[300],
              },
              '&.Mui-focusVisible': {
                borderColor: brand[400],
                outline: `3px solid ${alpha(brand[500], 0.5)}`,
                outlineOffset: '2px',
              },
            }),
          }),
        },
      },
      MuiList: {
        styleOverrides: {
          root: {
            padding: '8px',
            display: 'flex',
            flexDirection: 'column',
            gap: 0,
          },
        } as CSSProperties,
      },
      MuiListItem: {
        styleOverrides: {
          root: (props: any) => ({
            [`& .${svgIconClasses.root}`]: {
              width: '1rem',
              height: '1rem',
              color: (props.theme.vars || props.theme).palette.text.secondary,
            },
            [`& .${typographyClasses.root}`]: {
              fontWeight: 500,
            },
            [`& .${buttonBaseClasses.root}`]: {
              display: 'flex',
              gap: 8,
              padding: '2px 8px',
              borderRadius: (props.theme.vars || props.theme).shape.borderRadius,
              opacity: 0.7,
              '&.Mui-selected': {
                opacity: 1,
                backgroundColor: alpha(props.theme.palette.action.selected, 0.3),
                [`& .${svgIconClasses.root}`]: {
                  color: (props.theme.vars || props.theme).palette.text.primary,
                },
                '&:focus-visible': {
                  backgroundColor: alpha(props.theme.palette.action.selected, 0.3),
                },
                '&:hover': {
                  backgroundColor: alpha(props.theme.palette.action.selected, 0.5),
                },
              },
              '&:focus-visible': {
                backgroundColor: 'transparent',
              },
            },
          }),
        },
      },
      MuiListItemText: {
        styleOverrides: {
          primary: (props: any) => ({
            fontSize: props.theme.typography.body2.fontSize,
            fontWeight: 500,
            lineHeight: props.theme.typography.body2.lineHeight,
          }),
          secondary: (props: any) => ({
            fontSize: props.theme.typography.caption.fontSize,
            lineHeight: props.theme.typography.caption.lineHeight,
          }),
        },
      },
      MuiListSubheader: {
        styleOverrides: {
          root: (props: any) => ({
            backgroundColor: 'transparent',
            padding: '4px 8px',
            fontSize: props.theme.typography.caption.fontSize,
            fontWeight: 500,
            lineHeight: props.theme.typography.caption.lineHeight,
          }),
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            minWidth: 0,
          },
        },
      },
      MuiChip: {
        defaultProps: {
          size: 'small',
        } as ChipProps,
        styleOverrides: {
          root: (props: any) => ({
            border: '1px solid',
            borderRadius: '999px',
            [`& .${chipClasses.label}`]: {
              fontWeight: 600,
            },
            variants: [
              {
                props: {
                  color: 'default',
                } as CSSProperties,
                style: {
                  borderColor: gray[200],
                  backgroundColor: gray[100],
                  [`& .${chipClasses.label}`]: {
                    color: gray[500],
                  },
                  [`& .${chipClasses.icon}`]: {
                    color: gray[500],
                  },
                  ...props.theme.applyStyles('dark', {
                    borderColor: gray[700],
                    backgroundColor: gray[800],
                    [`& .${chipClasses.label}`]: {
                      color: gray[300],
                    },
                    [`& .${chipClasses.icon}`]: {
                      color: gray[300],
                    },
                  }),
                },
              },
              {
                props: {
                  color: 'success',
                } as CSSProperties,
                style: {
                  borderColor: green[200],
                  backgroundColor: green[50],
                  [`& .${chipClasses.label}`]: {
                    color: green[500],
                  },
                  [`& .${chipClasses.icon}`]: {
                    color: green[500],
                  },
                  ...props.theme.applyStyles('dark', {
                    borderColor: green[800],
                    backgroundColor: green[900],
                    [`& .${chipClasses.label}`]: {
                      color: green[300],
                    },
                    [`& .${chipClasses.icon}`]: {
                      color: green[300],
                    },
                  }),
                },
              },
              {
                props: {
                  color: 'error',
                } as CSSProperties,
                style: {
                  borderColor: red[100],
                  backgroundColor: red[50],
                  [`& .${chipClasses.label}`]: {
                    color: red[500],
                  },
                  [`& .${chipClasses.icon}`]: {
                    color: red[500],
                  },
                  ...props.theme.applyStyles('dark', {
                    borderColor: red[800],
                    backgroundColor: red[900],
                    [`& .${chipClasses.label}`]: {
                      color: red[200],
                    },
                    [`& .${chipClasses.icon}`]: {
                      color: red[300],
                    },
                  }),
                },
              },
              {
                props: { size: 'small' } as CSSProperties,
                style: {
                  maxHeight: 20,
                  [`& .${chipClasses.label}`]: {
                    fontSize: props.theme.typography.caption.fontSize,
                  },
                  [`& .${svgIconClasses.root}`]: {
                    fontSize: props.theme.typography.caption.fontSize,
                  },
                },
              },
              {
                props: { size: 'medium' } as CSSProperties,
                style: {
                  [`& .${chipClasses.label}`]: {
                    fontSize: props.theme.typography.caption.fontSize,
                  },
                } as CSSProperties,
              },
            ],
          } as CSSProperties),
        },
      },
      MuiTablePagination: {
        styleOverrides: {
          actions: {
            display: 'flex',
            gap: 8,
            marginRight: 6,
            [`& .${iconButtonClasses.root}`]: {
              minWidth: 0,
              width: 36,
              height: 36,
            },
          },
        },
      },
      MuiIcon: {
        defaultProps: {
          color: 'primary',
          fontSize: 'small',
        } as IconProps,
        styleOverrides: {
          root: {
            variants: [
              {
                props: {
                  fontSize: 'small',
                },
                style: {
                  fontSize: '1rem',
                },
              },
            ],
          } as CSSProperties,
        },
      },
      MuiDialog: {
        styleOverrides: {
          root: (props: any) => ({
            '& .MuiDialog-paper': {
              borderRadius: '10px',
              border: '1px solid',
              borderColor: (props.theme.vars || props.theme).palette.divider,
            },
          }),
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: (props: any) => ({
            height: 8,
            borderRadius: 8,
            backgroundColor: gray[200],
            ...props.theme.applyStyles('dark', {
              backgroundColor: gray[800],
            }),
          }),
        },
      },
      MuiLink: {
        defaultProps: {
          underline: 'none',
        } as LinkProps,
        styleOverrides: {
          root: (props: any) => ({
            color: (props.theme.vars || props.theme).palette.text.primary,
            fontWeight: 500,
            position: 'relative',
            textDecoration: 'none',
            width: 'fit-content',
            '&::before': {
              content: '""',
              position: 'absolute',
              width: '100%',
              height: '1px',
              bottom: 0,
              left: 0,
              backgroundColor: (props.theme.vars || props.theme).palette.text.secondary,
              opacity: 0.3,
              transition: 'width 0.3s ease, opacity 0.3s ease',
            },
            '&:hover::before': {
              width: 0,
            },
            '&:focus-visible': {
              outline: `3px solid ${alpha(brand[500], 0.5)}`,
              outlineOffset: '4px',
              borderRadius: '2px',
            },
          } as CSSProperties),
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: (props: any) => ({
            backgroundColor: (props.theme.vars || props.theme).palette.background.default,
          }),
        },
      },
      MuiPaginationItem: {
        styleOverrides: {
          root: (props: any) => ({
            '&.Mui-selected': {
              color: 'white',
              backgroundColor: (props.theme.vars || props.theme).palette.grey[900],
            },
            ...props.theme.applyStyles('dark', {
              '&.Mui-selected': {
                color: 'black',
                backgroundColor: (props.theme.vars || props.theme).palette.grey[50],
              },
            }),
          }),
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 'fit-content' },
          indicator: (props: any) => ({
            backgroundColor: (props.theme.vars || props.theme).palette.grey[800],
            ...props.theme.applyStyles('dark', {
              backgroundColor: (props.theme.vars || props.theme).palette.grey[200],
            }),
          }),
        },
      },
      MuiTab: {
        styleOverrides: {
          root: (props: any) => ({
            padding: '6px 8px',
            marginBottom: '8px',
            textTransform: 'none',
            minWidth: 'fit-content',
            minHeight: 'fit-content',
            color: (props.theme.vars || props.theme).palette.text.secondary,
            borderRadius: (props.theme.vars || props.theme).shape.borderRadius,
            border: '1px solid',
            borderColor: 'transparent',
            ':hover': {
              color: (props.theme.vars || props.theme).palette.text.primary,
              backgroundColor: gray[100],
              borderColor: gray[200],
            },
            [`&.${tabClasses.selected}`]: {
              color: gray[900],
            },
            ...props.theme.applyStyles('dark', {
              ':hover': {
                color: (props.theme.vars || props.theme).palette.text.primary,
                backgroundColor: gray[800],
                borderColor: gray[700],
              },
              [`&.${tabClasses.selected}`]: {
                color: '#fff',
              },
            }),
          }),
        },
      },
      MuiStepConnector: {
        styleOverrides: {
          line: (props: any) => ({
            borderTop: '1px solid',
            borderColor: (props.theme.vars || props.theme).palette.divider,
            flex: 1,
            borderRadius: '99px',
          }),
        },
      },
      MuiStepIcon: {
        styleOverrides: {
          root: (props: any) => ({
            color: 'transparent',
            border: `1px solid ${gray[400]}`,
            width: 12,
            height: 12,
            borderRadius: '50%',
            '& text': {
              display: 'none',
            },
            '&.Mui-active': {
              border: 'none',
              color: (props.theme.vars || props.theme).palette.primary.main,
            },
            '&.Mui-completed': {
              border: 'none',
              color: (props.theme.vars || props.theme).palette.success.main,
            },
            ...props.theme.applyStyles('dark', {
              border: `1px solid ${gray[700]}`,
              '&.Mui-active': {
                border: 'none',
                color: (props.theme.vars || props.theme).palette.primary.light,
              },
              '&.Mui-completed': {
                border: 'none',
                color: (props.theme.vars || props.theme).palette.success.light,
              },
            }),
            variants: [
              {
                props: { completed: true },
                style: {
                  width: 12,
                  height: 12,
                },
              },
            ],
          }),
        },
      },
      MuiStepLabel: {
        styleOverrides: {
          label: (props: any) => ({
            '&.Mui-completed': {
              opacity: 0.6,
              ...props.theme.applyStyles('dark', { opacity: 0.5 }),
            },
          }),
        },
      },
      MuiAccordion: {
        defaultProps: {
          elevation: 0,
          disableGutters: true,
        },
        styleOverrides: {
          root: (props: any) => ({
            padding: 4,
            overflow: 'clip',
            backgroundColor: (props.theme.vars || props.theme).palette.background.default,
            border: '1px solid',
            borderColor: (props.theme.vars || props.theme).palette.divider,
            ':before': {
              backgroundColor: 'transparent',
            },
            '&:not(:last-of-type)': {
              borderBottom: 'none',
            },
            '&:first-of-type': {
              borderTopLeftRadius: (props.theme.vars || props.theme).shape.borderRadius,
              borderTopRightRadius: (props.theme.vars || props.theme).shape.borderRadius,
            },
            '&:last-of-type': {
              borderBottomLeftRadius: (props.theme.vars || props.theme).shape.borderRadius,
              borderBottomRightRadius: (props.theme.vars || props.theme).shape.borderRadius,
            },
          }),
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: (props: any) => ({
            border: 'none',
            borderRadius: 8,
            '&:hover': { backgroundColor: gray[50] },
            '&:focus-visible': { backgroundColor: 'transparent' },
            ...props.theme.applyStyles('dark', {
              '&:hover': { backgroundColor: gray[800] },
            }),
          }),
        },
      },
      MuiAccordionDetails: {
        styleOverrides: {
          root: { mb: 20, border: 'none' },
        },
      },
      MuiPaper: {
        defaultProps: {
          elevation: 0,
        },
      },
      MuiCard: {
        styleOverrides: {
          root: (props: any) => {
            return {
              padding: 16,
              gap: 16,
              transition: 'all 100ms ease',
              backgroundColor: gray[50],
              borderRadius: (props.theme.vars || props.theme).shape.borderRadius,
              border: `1px solid ${(props.theme.vars || props.theme).palette.divider}`,
              boxShadow: 'none',
              ...props.theme.applyStyles('dark', {
                backgroundColor: gray[800],
              }),
              variants: [
                {
                  props: {
                    variant: 'outlined',
                  },
                  style: {
                    border: `1px solid ${(props.theme.vars || props.theme).palette.divider}`,
                    boxShadow: 'none',
                    background: 'hsl(0, 0%, 100%)',
                    ...props.theme.applyStyles('dark', {
                      background: alpha(gray[900], 0.4),
                    }),
                  },
                },
              ],
            };
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: 0,
            '&:last-child': { paddingBottom: 0 },
          },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          root: {
            padding: 0,
          },
        },
      },
      MuiCardActions: {
        styleOverrides: {
          root: {
            padding: 0,
          },
        },
      },
      ...props.themeComponents
    };
    return disableCustomTheme
      ? {}
      : createTheme({
        cssVariables: {
          colorSchemeSelector: 'data-mui-color-scheme',
          cssVarPrefix: 'template',
        },
        colorSchemes,
        typography,
        shadows,
        shape,
        components: components
      });
  }, [disableCustomTheme, props.themeComponents]);
  if (disableCustomTheme) {
    return <Fragment>{children}</Fragment>;
  }
  return (
    <ThemeProvider theme={theme} disableTransitionOnChange>
      {children}
    </ThemeProvider>
  );
}
